import { useRef, useState, useEffect } from "react";
import { DialogContent, Button, Divider, Paper, Tooltip, TextField, Grid, Box, IconButton } from "@mui/material";
import { RemoveCircleOutline, Save } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { updateActiveTask, Util } from "../../util/Util";
import { mapTasksWithActivation, refreshEmptyActiveTaskDate } from "../../util/taskActivation";
import { loadPendingTasks, persistPendingTasks, revivePendingItems, useSkipFirstRun } from "../../util/pendingTaskStorage";
import { usePendingWorkSessions } from "../../hooks/usePendingWorkSessions";
import { flushItemTableItems } from "../../util/itemTableFlush";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Item } from "./SupplyContext";
import SupplyDetail from "./SupplyDetail";
import TaskPane from "../../components/TaskPane";
import SupplyItemDetail from "./SupplyItemDetail";
import { LocalShippingOutlined } from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { useDepotAccess } from "../../hooks/useDepotAccess";
import { toast } from "react-toastify";
import { SupplyTask, useSupply } from './SupplyContext';
import { formatSupplyExpiry, isDueDateValidForSupply } from "./supplyHelpers";
import { useUI } from "../../providers/MoleUIContext";

import { DepotEnum, LocalStorageKeys, MYSQL_LIMITS, OperationEnum } from "../../util/constants";
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { useAppSocket } from "../../providers/AppSocketContext";
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ItemTable from "../../components/ItemTable";
import { applySavedCatalogOption, CatalogItemPickerProvider } from "../../components/CatalogItemActions";
import { v4 } from "uuid";

import { addMoney } from "../../util/quantity";
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	onSuccess?: () => void;
}

const SupplyModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const [taskHash, setTaskHash] = useState<string | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [renderChildren, setRenderChildren] = useState(false);
	const [tasksOpen, setTasksOpen] = useState(false);
	const [validateDetail, setValidateDetail] = useState(false);

	const { api, session, access } = useAuth();
	const { ensureRoute } = useDepotAccess();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { pendingTasks, setPendingTasks, fetchedTask, setFetchedTask, activeTask, addNewTask, setItems, items, apiRef } = useSupply();

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor();
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";

	const prevTaskIdRef = useRef<string | null>(null);
	const validateBtnRef = useRef<HTMLButtonElement>(null);
	const changeRef = useRef<boolean>(false);
	const skipFirstPersist = useSkipFirstRun();

	const reviveSupplyTask = (task: any): SupplyTask<Item> =>
		refreshEmptyActiveTaskDate({
			...task,
			createdAt: new Date(task.createdAt),
			updatedAt: new Date(task.updatedAt),
			items: revivePendingItems(task.items, (item: any) => new Item(item)),
			is_public: Boolean(task.is_public),
			server_v: Number(task.server_v || 0),
			total: function () {
				return (this.items ?? []).reduce((sum: number, item: { total: number }) => addMoney(sum, item.total), 0);
			},
		});

	const { userId, removeRemote, setPublic } = usePendingWorkSessions({
		workType: "supply",
		enabled: Boolean(isOpen && !bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
		reviveTask: reviveSupplyTask,
		storageKey: LocalStorageKeys.SUPPLY_TASKS,
	});

	const { createdAt, updatedAt, active, items: itms, ...bundleDetails } = activeTask || {};

	const bundleItems = itms
		? Object.values(itms).map(({ ...rest }) => rest)
		: [];

	const canValidate = activeTask && Object.keys(activeTask.items!).length > 0
		&& validateDetail
		&& activeTask.date // TODO: validate this 
		&& isDueDateValidForSupply(activeTask.date, activeTask.due_date)
		&& (bundle ? taskHash !== Util.hashObject(JSON.stringify(bundleDetails) + JSON.stringify(bundleItems)) : true);

	const fetchData = async () => {
		setLoadingMessage("FETCH");

		const res = await api.get(`/supplies/${bundle.batch}`);

		if (res) {
			if (res.status === 200) {
				res.data.records.sort((a: any, b: any) => a.item_name.localeCompare(b.item_name));
				setFetchedTask(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setLoadingMessage(null);
	};

	const onSubmit = () => {
		setConfirmModalOpen(true);
	};

	const handleConfirm = async () => {
		if (!activeTask) return;

		if (!Util.checkDating(activeTask.date, access, 'supply')) {
			return;
		}

		if (!ensureRoute(OperationEnum.ENTRY, DepotEnum.EXTERNAL_IN, activeTask.depot)) {
			return;
		}

		const batch = bundle ? activeTask.id : v4();

		const details = {
			batch: batch,
			supplier: activeTask.supplier,
			receiver: activeTask.receiver,
			depot: activeTask.depot,
			sup_date: activeTask.date,
			due_date: activeTask.due_date || null,
			ref: activeTask.reference,
			description: activeTask.description
		};

		const taskItems = flushItemTableItems<Item>(apiRef) ?? activeTask.items!;
		const records = taskItems.map(({ id, item, price, quantity, expiry }) => ({ item: id, item_desc: item, unit_cost: price, quantity, batch: batch, expiry: formatSupplyExpiry(expiry) }));

		const payload = {
			details: details,
			records: records
		}

		setLoadingMessage(bundle ? "UPDATE" : "SAVE");

		const res = !bundle ? await api.post("/supplies", payload) : await api.put(`/supplies/${batch}`, payload);

		if (res) {
			if (res.status === 201 || res.status === 204) {
				if (bundle) {
					toggle(); // Close modal
				} else {
					toast.success(t("common:toast.success.receipt"), { autoClose: 3000 });
					deleteTask(activeTask.id, { posted: true }); // Posted: clear it for everyone, not just its owner
				}
				changeRef.current = true;
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.integrity"), { autoClose: 3000 });
			} else {
				console.error("Failed to send data:", res);
			}
		}

		setLoadingMessage(null);
	}

	const deleteTask = (taskId: string, { posted = false }: { posted?: boolean } = {}) => {
		const existing = pendingTasks.find((task) => task.id === taskId);
		setPendingTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
		void removeRemote(taskId, existing?.server_v, { posted });
	};

	useEffect(() => {
		if (isOpen) {
			if (bundle) {
				addNewTask();
			} else {
				const parsedTasks = loadPendingTasks(LocalStorageKeys.SUPPLY_TASKS, reviveSupplyTask, session?.user_id);
				if (parsedTasks) {
					setPendingTasks(parsedTasks);
					return;
				}
				addNewTask();
			}
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !bundle) {
			const handleKeyDown = (e: KeyboardEvent) => {
				// Ctrl+Alt+Q — task pane (avoid bare Ctrl+Q: quits many browsers)
				if (!bundle && e.ctrlKey && e.altKey && e.key.toLowerCase() === "q") {
					e.preventDefault();
					setTasksOpen((open) => !open);
				} else if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "s") {
					e.preventDefault();
					validateBtnRef.current?.click();
				}
			};

			document.addEventListener("keydown", handleKeyDown);

			return () => {
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [isOpen]);

	useEffect(() => {
		if (skipFirstPersist()) return;

		// Always persist pending task when not in edit mode (i.e., when bundle is not provided)
		if (!bundle && isOpen) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			persistPendingTasks(LocalStorageKeys.SUPPLY_TASKS, session?.user_id, JSON.stringify(pendingTasks));
		}

		// If there are pending task and no active task, set the first task as active
		// This ensures that there is always an active task to work with
		// Empty tasks get today's date when activated if their date is stale
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prevTasks) =>
				mapTasksWithActivation(prevTasks, (_task, index) => index === 0)
			);
		}
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);

			// Lazy fetch data if in edit mode and only one pending task exists
			// This is to ensure that the data is fetched only when there is a single task to edit
			// This is because calling it immediately after addNewTask might fetch when there are no tasks yet
			// because setting state is asynchronous
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bundle) fetchData();
				prevTaskIdRef.current = activeTask.id;
			}

			// If in edit mode, calculate the task hash
			// This is used to determine if the task details have changed
			// and if the update can proceed
			if (bundle && activeTask && bundleItems.length > 0 && !taskHash) {
				setTaskHash(
					Util.hashObject(
						JSON.stringify(bundleDetails)
						+
						JSON.stringify(bundleItems)
					)
				);
			}
		} else {
			prevTaskIdRef.current = null;
		}
	}, [activeTask]);

	const cleanup = () => {
		setPendingTasks([]);
		setFetchedTask(null);
		setItems([]);
		setTaskHash(null);
		if (changeRef.current) {
			onSuccess?.();
		}
	};

	const toggleDrawer = (newOpen: boolean) => () => {
		setTasksOpen(newOpen);
	};

	const onDeleteClicked = (uniqueId: string) => {
		updateActiveTask(setPendingTasks, prev => ({
			items: prev.items
				? prev.items.filter(item => item.uniqueId !== uniqueId)
				: [],
		}));
	};

	const columns: GridColDef<Item>[] = [
		{
			field: 'rowNumber',
			headerName: '#',
			width: 20,
			valueGetter: (_, row) => apiRef.current!.getRowIndexRelativeToVisibleRows(row.uniqueId) + 1,
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value)
			? [
				{
					field: 'item_code',
					headerName: t('common:tables.itemCode'),
				},
			]
			: []),
		{
			field: 'item',
			headerName: t('common:tables.item'),
			flex: 2,
			minWidth: 250,
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_CATEGORY?.value)
			? [
				{
					field: 'category',
					headerName: t('common:tables.category'),
				},
			]
			: []),
		{
			field: 'quantity',
			headerName: t('common:tables.quantity'),
			type: 'number',
			editable: true
		},
		{
			field: 'price',
			headerName: t('common:tables.costPrice'),
			type: 'number',
			editable: true
		},
		// {
		// 	field: 'selling_price',
		// 	headerName: 'Selling Price',
		// 	type: 'number',
		// 	editable: true
		// },
		{
			field: 'total',
			headerName: t('common:tables.lineTotal'),
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
			type: 'number',
			description: t('common:tables.total'),
		},
		{
			field: 'expiry',
			type: 'date',
			headerName: t('common:tables.expiry'),
			editable: true,
			valueGetter: (_value, row) => {
				if (!row.expiry) return null;
				const date = row.expiry instanceof Date ? row.expiry : new Date(row.expiry);
				return Number.isNaN(date.getTime()) ? null : date;
			},
		},
		{
			field: 'actions',
			headerName: '',
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<Item>) => (
				<Tooltip title={t('common:ui.tooltips.delete')}>
					<IconButton color="error" onClick={() => { onDeleteClicked(params.row.uniqueId) }}>
						<RemoveCircleOutline />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxWidth: "95%", width: "95%", maxHeight: "95vh" }}
				disableEscapeKeyDown
				onTransitionExited={cleanup}
			>
				<MoleDialogHeader
					title={t("common:supply.modal.title")}
					subtitle={`${t("common:report.grandTotal")}: ${Util.formatCurrency(activeTask?.total() || 0, currency)}`}
					icon={<LocalShippingOutlined />}
					onClose={toggle}
					themeColor={themeColor}
					extra={
						!bundle ? (
							<Button
								variant="outlined"
								size="small"
								startIcon={<PendingActionsIcon />}
								onClick={() => toggleDrawer(true)()}
							>
								{t("common:ui.buttons.tasks")}
							</Button>
						) : undefined
					}
				/>
				<DialogContent>
					<Paper elevation={24}>
						{renderChildren &&
							<SupplyDetail
								setValidateCheck={setValidateDetail}
							/>
						}
						<Divider orientation="vertical" variant="middle" flexItem />
						{renderChildren &&
							< SupplyItemDetail
								bundle={fetchedTask?.records}
							/>
						}
					</Paper>
					{renderChildren &&
						<CatalogItemPickerProvider
							depotIds={[activeTask?.depot]}
							onItemSaved={(updated) => {
								setItems(applySavedCatalogOption(items, updated));
								updateActiveTask(setPendingTasks, (prev) => ({
									...prev,
									items: prev.items?.map((it) =>
										it.id === updated.id
											? new Item({
												...it,
												item: updated.ItemName || it.item,
												item_code: updated.ItemCode || it.item_code,
												isFractional: updated.IsFractional === true,
											})
											: it
									),
								}));
							}}
						>
							<ItemTable<Item, SupplyTask<Item>>
								activeTask={activeTask}
								setPendingTasks={setPendingTasks}
								columns={columns}
								itemConstructor={Item}
								bundle={fetchedTask?.records}
								items={items}
								checkStock={false}
								lineNameMode="strict"
								apiRef={apiRef}
							/>
						</CatalogItemPickerProvider>
					}
				</DialogContent>
				<MoleDialogActions sx={{ justifyContent: "stretch" }}>
					<Grid container spacing={1} alignItems="center" width="100%">
						<Grid sx={{ flexGrow: 1, minWidth: 0 }}>
							<TextField
								id="details"
								size="small"
								label={t("common:ui.labels.notes")}
								variant="filled"
								fullWidth
								type="text"
								value={activeTask?.description || ""}
								inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
								onChange={(e) => updateActiveTask(setPendingTasks, { description: e.target.value })}
								disabled={!activeTask}
							/>
						</Grid>
						<Grid
							sx={{
								width: { xs: "100%", sm: "100%", md: "auto" },
								display: "flex",
								justifyContent: { xs: "space-between", md: "flex-end" },
								alignItems: "center",
								gap: 1,
							}}
						>
							<Button
								ref={validateBtnRef}
								variant="contained"
								color="success"
								onClick={onSubmit}
								disabled={!canValidate}
								startIcon={<Save />}
							>
								{bundle ? t("common:ui.buttons.amendReceipt") : t("common:ui.buttons.receive")}
							</Button>
							<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
						</Grid>
					</Grid>
				</MoleDialogActions>

				{!bundle &&
					<TaskPane<Item, SupplyTask<Item>>
						tasks={pendingTasks}
						setTasks={setPendingTasks}
						addNewTask={addNewTask}
						tasksOpen={tasksOpen}
						onCallback={() => setTasksOpen(false)}
						currentUserId={userId}
						onTogglePublic={(task) => { void setPublic(task, !task.is_public); }}
						onDeleteTask={(id: string) => deleteTask(id)}
					/>
				}
			</MoleDialog>

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				< ConfirmDialog
					onConfirm={handleConfirm}
					isOpen={confirmModalOpen}
					setIsOpen={setConfirmModalOpen}
					title={t("common:dialog.supplyConfirmation")}
					confirmBtnColor="success" />
			}
		</Box>
	);
};

export default SupplyModal;
